import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Checkbox } from '@mantine/core';

interface ServiceFiltersModalProps {
    filters: Record<string, any[]>;
    allFilterOptions: Record<string, any[]>;
    availableFilterOptions: Record<string, any[]>;
    allAttributeKeys: string[];
    providerAttributeKeys: string[];
    onToggleFilter: (filterType: string, value: any) => void;
    defaultExpanded?: boolean;
    className?: string;
}

export default function ServiceFiltersColumn({
    className,
    filters,
    allFilterOptions,
    availableFilterOptions,
    allAttributeKeys,
    providerAttributeKeys,
    onToggleFilter,
    defaultExpanded = true,
}: ServiceFiltersModalProps) {
    const { t } = useTranslation();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    // Update expanded sections when data loads or defaultExpanded changes
    useEffect(() => {
        if (defaultExpanded) {
            const allSections = new Set<string>();
            allAttributeKeys.forEach(key => {
                if ((allFilterOptions[key]?.length || 0) > 1) allSections.add(key);
            });
            providerAttributeKeys.forEach(key => {
                if ((allFilterOptions[`provider_${key}`]?.length || 0) > 1) allSections.add(`provider_${key}`);
            });
            if (allFilterOptions.publishPrice?.length > 1) allSections.add('publishPrice');
            if (allFilterOptions.hasCost?.length > 1) allSections.add('hasCost');
            if (allFilterOptions.hasLimit?.length > 1) allSections.add('hasLimit');
            if (allFilterOptions.sellerName?.length > 1) allSections.add('sellerName');
            if (allFilterOptions.businessName?.length > 1) allSections.add('businessName');
            setExpandedSections(allSections);
        }
    }, [defaultExpanded, allAttributeKeys, providerAttributeKeys, allFilterOptions]);

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionKey)) {
                newSet.delete(sectionKey);
            } else {
                newSet.add(sectionKey);
            }
            return newSet;
        });
    };

    // Helper function to format attribute value for display
    const formatAttributeValue = (value: any): string => {
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'boolean') return value ? t('yes') : t('no');
        return String(value);
    };

    // Reusable function to render filter button
    const renderFilterButton = (
        filterType: string,
        value: any,
        index: number,
    ) => {
        const isSelected = (filters[filterType] || []).includes(value);
        const isAvailable = (availableFilterOptions[filterType] || []).includes(value);
        const isDisabled = !isSelected && !isAvailable;

        return (
            <div key={`${filterType}-${value}-${index}`}>
                <div
                    className={`
                        rounded-md
                        px-1 py-0.5
                        mb-0.25
                        text-xs
                        text-gray-700 font-medium
                        max-w-[300px]
                        ${isDisabled
                            ? 'opacity-30'
                            : 'cursor-pointer hover:bg-gray-200'
                        }
                        ${isSelected ? 'bg-blue-100 text-blue-700' : ''}
                    `}
                    onClick={() => !isDisabled && onToggleFilter(filterType, value)}
                >
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            color='black'
                            checked={isSelected}
                            onChange={() => { }}
                        />
                        <span className="flex-1 min-w-0 break-words">
                            {formatAttributeValue(value)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const filterTitleClassname = 'text-xs font-bold text-gray-900 cursor-pointer p-1 flex items-center justify-between';
    const filterSectionClassName = 'border-b border-gray-100 space-y-1 p-1';

    return (
        <div className={className}>
            {/* Provider self_attrs filters */}
            {providerAttributeKeys.map(key => {
                const sectionKey = `provider_${key}`;
                const options = allFilterOptions[sectionKey] || [];
                const isExpanded = expandedSections.has(sectionKey);
                if (options.length <= 1) return null;
                return (
                    <div
                        key={sectionKey}
                        className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection(sectionKey)}
                        >
                            <span>{t(`${key}`)}</span>
                        </div>
                        {isExpanded && (
                            <div className="px-1 pb-1">
                                {options.map((value, index) =>
                                    renderFilterButton(sectionKey, value, index)
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Service self_attrs filters */}
            {allAttributeKeys.map(key => {
                const options = allFilterOptions[key] || [];
                const isExpanded = expandedSections.has(key);
                if (options.length <= 1) return null;

                return (
                    <div
                        key={key}
                        className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection(key)}
                        >
                            <span>{t(`${key}`)}</span>
                        </div>
                        {isExpanded && (
                            <div className="px-1 sm:px-2 pb-2">
                                {options.map((value, index) =>
                                    renderFilterButton(key, value, index)
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Publish Price Filter */}
            {allFilterOptions.publishPrice?.length > 1 && (
                <div className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection('publishPrice')}
                    >
                        <span>{t('publish_price')}</span>
                    </div>
                    {expandedSections.has('publishPrice') && (
                        <div className="px-1 sm:px-2 pb-2">
                            {allFilterOptions.publishPrice.map((value, index) =>
                                renderFilterButton('publishPrice', value, index)
                            )}
                        </div>
                    )}
                </div>
            )
            }

            {/* Has Cost Filter */}
            {
                allFilterOptions.hasCost?.length > 1 && (
                    <div className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection('hasCost')}
                        >
                            <span>{t('cost')}</span>
                        </div>
                        {expandedSections.has('hasCost') && (
                            <div className="px-1 sm:px-2 pb-2">
                                {allFilterOptions.hasCost.map((value, index) =>
                                    renderFilterButton('hasCost', value, index)
                                )}
                            </div>
                        )}
                    </div>
                )
            }

            {/* Has Limit Filter */}
            {
                allFilterOptions.hasLimit?.length > 1 && (
                    <div className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection('hasLimit')}
                        >
                            <span>{t('default_limit')}</span>
                        </div>
                        {expandedSections.has('hasLimit') && (
                            <div className="px-1 sm:px-2 pb-2">
                                {allFilterOptions.hasLimit.map((value, index) =>
                                    renderFilterButton('hasLimit', value, index)
                                )}
                            </div>
                        )}
                    </div>
                )
            }

            {/* Seller Names */}
            {
                allFilterOptions.sellerName?.length > 1 && (
                    <div className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection('sellerName')}
                        >
                            <span>{t('sold_by_long')}</span>
                        </div>
                        {expandedSections.has('sellerName') && (
                            <div className="px-1 sm:px-2 pb-2">
                                {allFilterOptions.sellerName.map((seller, index) =>
                                    renderFilterButton('sellerName', seller, index)
                                )}
                            </div>
                        )}
                    </div>
                )
            }

            {/* Business Names with Provider Icon */}
            {
                allFilterOptions.businessName?.length > 1 && (
                    <div className={filterSectionClassName}>
                        <div
                            className={filterTitleClassname}
                            onClick={() => toggleSection('businessName')}
                        >
                            <span>{t('operated_by')}</span>
                        </div>
                        {expandedSections.has('businessName') && (
                            <div className="px-1 sm:px-2 pb-2">
                                {allFilterOptions.businessName.map((business, index) =>
                                    renderFilterButton('businessName', business, index)
                                )}
                            </div>
                        )}
                    </div>
                )
            }

        </div >
    );
}